import { PickerGroup, PickerItem } from './GroupedPicker';
import { DirectoryTenant, DirectoryUser, GLOBAL_ADMINS_KEY, UserList } from './useUserDirectory';

const byName = (a: { tenantName: string }, b: { tenantName: string }) =>
  a.tenantName.localeCompare(b.tenantName, undefined, { sensitivity: 'base' });

const toItem = (user: DirectoryUser): PickerItem => ({ value: user.email, label: user.name || user.email, detail: user.email });

/** Top-level sections that are only headings; open from the start (opening them costs no API call). */
export const RESELLERS_SECTION = 'resellers';
export const TENANTS_SECTION = 'tenants';

/**
 * The User filter's sections for the signed-in user's directory tree, in this order:
 * - Globaladmin: Global admins; Resellers (each reseller: its users, then each of its tenants); Tenants no reseller manages.
 * - Reseller: Global admins; the reseller's own users; its Tenants.
 * - Tenant admin: Global admins; the tenant's users.
 * Every list of users is lazy: it loads (`loadUsers`) the first time its section is opened.
 */
export const userDirectoryGroups = (
  tree: DirectoryTenant,
  lists: Record<string, UserList>,
  loadUsers: (key: string) => void,
): PickerGroup[] => {
  const lazy = (key: string) => {
    const list = lists[key];
    return {
      items: list?.status === 'ready' ? list.users.map(toItem) : undefined,
      loading: list?.status === 'loading',
      failed: list?.status === 'failed',
      onExpand: () => loadUsers(key),
      emptyText: 'No users.',
    };
  };

  // A tenant's own users. `path` keeps keys unique: a tenant mapped to two resellers shows under both.
  const tenantUsers = (tenant: DirectoryTenant, path: string, label = tenant.tenantName): PickerGroup => ({
    key: `${path}/${tenant.tenantId}`,
    label,
    tag: tenant.tenantType === 'Reseller' ? 'Reseller' : tenant.tenantType === 'GlobalAdmin' ? 'Global admin' : 'Tenant',
    tone: tenant.tenantType === 'Reseller' ? 'blue' : tenant.tenantType === 'GlobalAdmin' ? 'red' : 'gray',
    count: tenant.userCount,
    ...lazy(tenant.tenantId),
  });

  const globalAdmins: PickerGroup = {
    key: GLOBAL_ADMINS_KEY,
    label: 'Global admins',
    tag: 'Global admin',
    tone: 'red',
    count: lists[GLOBAL_ADMINS_KEY]?.status === 'ready' ? lists[GLOBAL_ADMINS_KEY].users.length : undefined,
    ...lazy(GLOBAL_ADMINS_KEY),
  };

  const tenantsSection = (tenants: DirectoryTenant[], label: string): PickerGroup[] =>
    tenants.length === 0
      ? []
      : [{ key: TENANTS_SECTION, label, count: tenants.length, groups: [...tenants].sort(byName).map((t) => tenantUsers(t, TENANTS_SECTION)) }];

  if (tree.tenantType === 'GlobalAdmin') {
    const resellers = tree.tenants.filter((t) => t.tenantType === 'Reseller').sort(byName);
    const direct = tree.tenants.filter((t) => t.tenantType !== 'Reseller');
    return [
      globalAdmins,
      ...(resellers.length === 0
        ? []
        : [
            {
              key: RESELLERS_SECTION,
              label: 'Resellers',
              count: resellers.length,
              groups: resellers.map((reseller) => {
                const path = `${RESELLERS_SECTION}/${reseller.tenantId}`;
                return {
                  key: path,
                  label: reseller.tenantName,
                  tag: 'Reseller',
                  tone: 'blue' as const,
                  count: reseller.tenants.length,
                  groups: [
                    tenantUsers(reseller, path, 'Reseller users'),
                    ...[...reseller.tenants].sort(byName).map((customer) => tenantUsers(customer, path)),
                  ],
                };
              }),
            },
          ]),
      ...tenantsSection(direct, 'Tenants'),
    ];
  }

  if (tree.tenantType === 'Reseller') {
    return [globalAdmins, tenantUsers(tree, 'own'), ...tenantsSection(tree.tenants, 'Tenants')];
  }

  return [globalAdmins, tenantUsers(tree, 'own')];
};

/** A loaded user's name for an email, for the closed picker; the email itself when not loaded. */
export const directoryUserLabel = (email: string, lists: Record<string, UserList>): string => {
  const lower = email.toLowerCase();
  for (const list of Object.values(lists)) {
    const user = list.users.find((u) => u.email.toLowerCase() === lower);
    if (user) return user.name && user.name !== user.email ? `${user.name} (${user.email})` : user.email;
  }
  return email;
};
